import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PromiseClient } from '@fc-shared/utils/request-utils/promise-client';
import { PromiseResult } from '@fc-shared/utils/request-utils/toPromise';
import { decodePolyline6 } from '@fc-shared/utils/polyline';
import { KILOMETERS_TO_MILES } from '@fc-shared/utils/units';
import { environment } from 'src/environments/environment';

// Number of alternate paths to request per leg. Valhalla returns up to
// this many when it finds meaningfully different routes for a pair.
const ALTERNATE_COUNT = 2;

export interface RouteGeometry {
  type: 'LineString';
  coordinates: [number, number][];
}

export interface RouteResult {
  distance: number; // meters
  duration: number; // seconds
  geometry: RouteGeometry;
}

// One segment of the task route (checkpoint A -> B). `options[0]` is the
// fastest; the rest are alternates the driver can pick per leg.
export interface RouteLeg {
  options: RouteResult[];
}

export interface RouteResponse {
  legs: RouteLeg[];
}

interface ValhallaLeg {
  shape: string;
}

interface ValhallaTrip {
  status: number;
  legs: ValhallaLeg[];
  summary: { length: number; time: number };
}

interface ValhallaRouteResponse {
  trip?: ValhallaTrip;
  alternates?: { trip?: ValhallaTrip }[];
}

// Shape of the /locate response (verbose mode). We read the posted speed
// limit off the closest edge to the queried point; `distance` is how far
// (meters) that edge is from the point, used to reject bad snaps.
interface ValhallaLocateEdge {
  distance?: number;
  edge_info?: { speed_limit?: number };
}

interface ValhallaLocateResult {
  edges?: ValhallaLocateEdge[];
}

// Valhalla stores speed limits in km/h; 0 means "no data" and 255 means
// "unlimited". The fleet is US-based, so we surface mph like Tiger did.
const VALHALLA_UNLIMITED_KMH = 255;
// If the nearest road is farther than this from the point, the vehicle isn't
// on it (sparse-graph snaps can land kilometers away) — don't trust its limit.
const MAX_SNAP_METERS = 30;

@Injectable({
  providedIn: 'root',
})
export class ValhallaService {
  private readonly baseUrl = environment.valhallaUrl;
  private http = inject(PromiseClient);

  // Valhalla only returns alternates for 2-location requests, so each
  // checkpoint pair is routed independently to get per-leg alternates.
  async getRoute(
    points: google.maps.LatLngLiteral[],
  ): PromiseResult<RouteResponse> {
    if (points.length < 2) {
      return [null, { legs: [] }];
    }

    const pairs: [google.maps.LatLngLiteral, google.maps.LatLngLiteral][] = [];
    for (let i = 0; i < points.length - 1; i++) {
      pairs.push([points[i], points[i + 1]]);
    }

    const results = await Promise.all(pairs.map((pair) => this.fetchLeg(pair)));

    const legs: RouteLeg[] = [];
    for (const [error, options] of results) {
      if (error || !options.length) {
        return [error ?? null, { legs: [] }];
      }
      legs.push({ options });
    }

    return [null, { legs }];
  }

  // Posted speed limit (mph) for the road nearest the given point, or null
  // when Valhalla has no posted limit for it.
  async getSpeedLimit(
    point: google.maps.LatLngLiteral,
  ): PromiseResult<number | null> {
    const body = {
      locations: [{ lat: point.lat, lon: point.lng }],
      costing: 'auto',
      verbose: true,
    };

    const [error, response] = await this.http.post<ValhallaLocateResult[]>(
      `${this.baseUrl}/locate`,
      body,
    );
    if (error) {
      return [error, null];
    }

    // /locate can return several correlated edges (e.g. both directions of the
    // same road, or nearby streets) in no guaranteed order — take the closest.
    const closestEdge = (
      response?.[0]?.edges ?? []
    ).reduce<ValhallaLocateEdge | null>(
      (closest, edge) =>
        (edge.distance ?? Infinity) < (closest?.distance ?? Infinity)
          ? edge
          : closest,
      null,
    );
    if (!closestEdge || (closestEdge.distance ?? Infinity) > MAX_SNAP_METERS) {
      return [null, null];
    }

    const speedLimitKmh = closestEdge.edge_info?.speed_limit ?? 0;
    if (!speedLimitKmh || speedLimitKmh >= VALHALLA_UNLIMITED_KMH) {
      return [null, null];
    }

    // Round to the nearest 5 mph to clean up km/h -> mph conversion artifacts;
    // US posted limits are always multiples of 5.
    const mph = Math.round((speedLimitKmh * KILOMETERS_TO_MILES) / 5) * 5;
    return [null, mph];
  }

  private async fetchLeg(
    pair: [google.maps.LatLngLiteral, google.maps.LatLngLiteral],
  ): Promise<[HttpErrorResponse | null, RouteResult[]]> {
    const body = {
      locations: pair.map((point) => ({ lat: point.lat, lon: point.lng })),
      costing: 'auto',
      units: 'kilometers',
      directions_type: 'none',
      alternates: ALTERNATE_COUNT,
    };

    const [error, response] = await this.http.post<ValhallaRouteResponse>(
      `${this.baseUrl}/route`,
      body,
    );
    if (error) {
      return [error, []];
    }

    const trips = [
      response?.trip,
      ...(response?.alternates ?? []).map((alternate) => alternate.trip),
    ];
    const options = trips
      .map((trip) => this.tripToRoute(trip))
      .filter((route): route is RouteResult => route !== null);

    return [null, options];
  }

  private tripToRoute(trip: ValhallaTrip | undefined): RouteResult | null {
    if (!trip || trip.status !== 0 || !trip.legs?.length) {
      return null;
    }

    // Concatenate each leg's snapped shape, dropping the point shared with
    // the previous leg so waypoints don't produce duplicate vertices.
    const coordinates: [number, number][] = [];
    for (const leg of trip.legs) {
      const legCoordinates = decodePolyline6(leg.shape);
      if (coordinates.length && legCoordinates.length) {
        legCoordinates.shift();
      }
      coordinates.push(...legCoordinates);
    }

    if (coordinates.length < 2) {
      return null;
    }

    return {
      distance: trip.summary.length * 1000, // km -> meters
      duration: trip.summary.time,
      geometry: { type: 'LineString', coordinates },
    };
  }
}
